"use client";

import { useEffect, useRef } from "react";
import { FilledButton } from "../buttons/filled-button";
import { OutlineButton } from "../buttons/outline-button";

export type ConfirmationVariant = "danger" | "warning" | "info" | "success";

export interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: (value: boolean) => void;
    title?: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: ConfirmationVariant;
    isLoading?: boolean;
    isLoadingText?: string;
}

const variantConfig: Record<
    ConfirmationVariant,
    { iconBg: string; confirmBtn: string; icon: React.ReactNode }
> = {
    danger: {
        iconBg: "bg-red-100 text-red-600",
        confirmBtn: "bg-red-600 hover:bg-red-700 text-white",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
        ),
    },
    warning: {
        iconBg: "bg-amber-100 text-amber-600",
        confirmBtn: "bg-amber-500 hover:bg-amber-600 text-white",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
            </svg>
        ),
    },
    info: {
        iconBg: "bg-blue-100 text-blue-600",
        confirmBtn: "bg-blue-600 hover:bg-blue-700 text-white",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
            </svg>
        ),
    },
    success: {
        iconBg: "bg-emerald-100 text-emerald-600",
        confirmBtn: "bg-emerald-600 hover:bg-emerald-700 text-white",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
};

const ConfirmationModal = ({
    isOpen,
    onClose,
    title = "Are you sure?",
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "danger",
    isLoading = false,
    isLoadingText = "Removing...",
}: ConfirmationModalProps) => {
    const cfg = variantConfig[variant];

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !isLoading) onClose(false);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [isOpen, isLoading, onClose]);

    if (!isOpen) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => !isLoading && onClose(false)}
            />
            {/* Panel */}
            <div
                className="relative w-full max-w-md sm:max-w-lg md:max-w-xl rounded-2xl bg-white shadow-2xl"
            >
                <div className="p-6">
                    {/* Icon + text */}
                    <div className="flex items-start gap-4">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.iconBg}`}>
                            {cfg.icon}
                        </div>
                        <div className="pt-0.5">
                            <h2 id="confirm-title" className="text-lg font-semibold text-gray-900">
                                {title}
                            </h2>
                            {description && (
                                <p className="mt-1 text-lg text-gray-500">{description}</p>
                            )}
                        </div>
                    </div>
                    {/* Buttons */}
                    <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <OutlineButton
                            type="button"
                            onClick={() => onClose(false)}
                            disabled={isLoading}
                            className={`${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            {cancelLabel}
                        </OutlineButton>
                        <FilledButton
                            type="button"
                            onClick={() => onClose(true)}
                            disabled={isLoading}
                            className={`${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            {isLoading ? isLoadingText : confirmLabel}
                        </FilledButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;